import React, { useEffect, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Heart,
  Minus,
  Plus,
  RotateCcw,
  ShieldCheck,
  Truck,
} from "lucide-react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getErrorMessage,
  getImageUrl,
  formatCurrency,
} from "../../core/api.jsx";
import {
  createProductReview,
  getMyProductReview,
  getProductDetail,
  getProductReviews,
  updateProductReview,
} from "../../core/services/catalogService";
import { useAuth } from "../../contexts/AuthContext";
import { useCart } from "../../contexts/CartContext";
import { useWishlist } from "../../contexts/WishlistContext";
import { useToast } from "../../contexts/ToastContext";
import { activeVariants, variantOffer } from "../../shared/variantUtils";

export default function ProductDetail() {
  const { slug, productCode } = useParams();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [imageIndex, setImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [variantId, setVariantId] = useState("");
  const [colorId, setColorId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [myReview, setMyReview] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewError, setReviewError] = useState("");
  const [reviewMessage, setReviewMessage] = useState("");
  const { token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { toggleWishlist, wishlistIds } = useWishlist();
  const { notify } = useToast();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setProduct(null);
    getProductDetail(slug, productCode)
      .then(async (data) => {
        if (!alive) return;
        setProduct(data.product);
        const variants = activeVariants(data.product);
        const initial =
          variants.find((variant) => Number(variant.stock) > 0) || variants[0];
        setVariantId(initial?._id || "");
        const initialColors = initial?.colors || [];
        setColorId(initialColors.length === 1 ? initialColors[0]._id : "");
        const [reviewResult, ownReviewResult] = await Promise.all([
          getProductReviews(data.product._id).catch(() => null),
          token
            ? getMyProductReview(data.product._id).catch(() => null)
            : Promise.resolve(null),
        ]);
        if (alive) {
          setReviews(reviewResult?.reviews || []);
          const ownReview = ownReviewResult?.review || null;
          setMyReview(ownReview);
          setReviewRating(Number(ownReview?.rating || 5));
          setReviewComment(ownReview?.comment || "");
        }
      })
      .catch((requestError) => {
        if (alive)
          setError(
            getErrorMessage(requestError, "We couldn't find this product."),
          );
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [slug, productCode, token]);

  const saveReview = async (event) => {
    event.preventDefault();
    if (!token) return;
    const comment = reviewComment.trim();
    if (!comment) {
      setReviewError("Please write a short comment before submitting.");
      return;
    }
    setReviewBusy(true);
    setReviewError("");
    setReviewMessage("");
    try {
      const payload = { rating: reviewRating, comment };
      const data = myReview?._id
        ? await updateProductReview(myReview._id, payload)
        : await createProductReview(product._id, payload);
      const savedReview = data.review;
      setMyReview(savedReview);
      setReviewComment(savedReview?.comment || comment);
      setReviewRating(Number(savedReview?.rating || reviewRating));
      setReviews((current) => {
        const exists = current.some(
          (review) => review._id === savedReview?._id,
        );
        return exists
          ? current.map((review) =>
              review._id === savedReview._id ? savedReview : review,
            )
          : [savedReview, ...current];
      });
      setReviewMessage(
        myReview
          ? "Your review has been updated."
          : "Thanks for sharing your review.",
      );
      getProductDetail(slug, productCode)
        .then((result) => setProduct(result.product))
        .catch(() => {});
    } catch (requestError) {
      setReviewError(getErrorMessage(requestError));
    } finally {
      setReviewBusy(false);
    }
  };

  const save = async () => {
    try {
      await toggleWishlist(product);
      notify(
        wishlistIds.includes(String(product._id))
          ? "Removed from saved items."
          : "Saved for later.",
      );
    } catch (requestError) {
      notify(requestError.message, "error");
    }
  };

  const add = async () => {
    try {
      if (activeVariants(product).length && !variantId) {
        notify("Please select a variant first.", "error");
        return;
      }
      const chosen = activeVariants(product).find(
        (item) => String(item._id) === String(variantId),
      );
      if ((chosen?.colors || []).length && !colorId) {
        notify("Please select a color first.", "error");
        return;
      }
      await addToCart(product, quantity, variantId, colorId);
      notify("Added to your bag.");
    } catch (requestError) {
      notify(requestError.message, "error");
    }
  };

  const buyNow = async () => {
    try {
      if (activeVariants(product).length && !variantId) {
        notify("Please select a variant first.", "error");
        return;
      }
      const chosen = activeVariants(product).find(
        (item) => String(item._id) === String(variantId),
      );
      if ((chosen?.colors || []).length && !colorId) {
        notify("Please select a color first.", "error");
        return;
      }
      await addToCart(product, quantity, variantId, colorId);
      navigate("/checkout");
    } catch (requestError) {
      notify(requestError.message, "error");
    }
  };

  if (loading)
    return (
      <main className="page-shell product-detail-loading">
        <div className="skeleton-product" />
        <div className="skeleton-copy">
          <span />
          <span />
          <span />
          <span />
        </div>
      </main>
    );
  if (error || !product)
    return (
      <main className="page-shell">
        <div className="empty-state">
          <span className="empty-state__icon">✳</span>
          <h2>We couldn't find that one.</h2>
          <p>{error || "The product may no longer be available."}</p>
          <Link className="button button--primary" to="/shop">
            <ArrowLeft size={17} /> Back to the shop
          </Link>
        </div>
      </main>
    );

  const images = product.images?.length ? product.images : [];
  const variants = activeVariants(product);
  const selectedVariant = variants.find(
    (variant) => String(variant._id) === String(variantId),
  );
  const colors = selectedVariant?.colors || [];
  const selectedColor = colors.find(
    (color) => String(color._id) === String(colorId),
  );
  const offer = variantOffer(selectedVariant, product);
  const price = offer.price;
  const compareAt = offer.compareAt;
  const hasDiscount = compareAt > price;
  const availableStock = Number(
    selectedColor?.stock ?? selectedVariant?.stock ?? product.stock,
  );
  const available = colors.length
    ? Boolean(selectedColor) && availableStock > 0
    : availableStock > 0;
  const chooseVariant = (variant) => {
    if (!variant) return;
    const nextColors = variant.colors || [];
    setVariantId(variant._id);
    setColorId(nextColors.length === 1 ? nextColors[0]._id : "");
    setQuantity(1);
  };
  const chooseColor = (color) => {
    setColorId(color._id);
    setQuantity(1);
  };

  return (
    <main className="page-shell product-page">
      <div className="breadcrumbs">
        <Link to="/">Home</Link>
        <span>/</span>
        <Link to="/shop">Shop</Link>
        <span>/</span>
        <span>{product.name}</span>
      </div>
      <div className="product-detail">
        <section
          className={`product-gallery${images.length > 1 ? " product-gallery--has-thumbs" : ""}`}
        >
          <div
            className={`product-gallery__layout${images.length > 1 ? " has-thumbs" : ""}`}
          >
            <div className="product-gallery__main">
              {images[imageIndex] ? (
                <img src={getImageUrl(images[imageIndex])} alt={product.name} />
              ) : (
                <div className="image-placeholder">
                  Sugam
                  <br />
                  Store
                </div>
              )}
              {hasDiscount && (
                <span className="product-gallery__badge">
                  Save {Math.round((1 - price / compareAt) * 100)}%
                </span>
              )}
            </div>
            {images.length > 1 && (
              <div className="product-gallery__thumbs">
                {images.map((image, index) => (
                  <button
                    className={index === imageIndex ? "is-active" : ""}
                    key={`${image}-${index}`}
                    onClick={() => setImageIndex(index)}
                  >
                    <img
                      src={getImageUrl(image)}
                      alt={`${product.name}, view ${index + 1}`}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>
        <section className="product-info">
          <div className="product-info__rating">
            <span>★</span> {Number(product.ratings || 0).toFixed(1)}{" "}
            <span className="muted">· {product.numOfReviews || 0} reviews</span>
          </div>
          <h1>{product.name}</h1>
          <div className="breadcrumbs product-info__category">
            <span>{product.category}</span>
            {product.subCategory && <span>{product.subCategory}</span>}
            {product.productCode && <span>{product.productCode}</span>}
          </div>
          <div className="product-info__price">
            <strong>{formatCurrency(price)}</strong>
            {hasDiscount && (
              <>
                <del>{formatCurrency(compareAt)}</del>
                <span>Save {Math.round((1 - price / compareAt) * 100)}%</span>
              </>
            )}
          </div>
          <p className="product-info__short">
            {product.shortDescription ||
              "A thoughtful pick for your everyday. Made to be used, loved and enjoyed."}
          </p>

          {variants.length > 0 && (
            <div className="variant-picker">
              <div className="variant-picker__group">
                <span className="variant-picker__label">Choose variant</span>
                <div className="variant-picker__options">
                  {variants.map((variant) => (
                    <button
                      type="button"
                      key={variant._id}
                      className={`variant-picker__option${String(variant._id) === String(variantId) ? " is-selected" : ""}${Number(variant.stock) < 1 ? " is-sold-out" : ""}`}
                      onClick={() => chooseVariant(variant)}
                    >
                      {variant.name}
                    </button>
                  ))}
                </div>
              </div>
              {colors.length > 0 && (
                <div className="variant-picker__group">
                  <span className="variant-picker__label">Choose color</span>
                  <div className="variant-picker__options">
                    {colors.map((color) => (
                      <button
                        type="button"
                        key={color._id}
                        className={`variant-picker__option${String(color._id) === String(colorId) ? " is-selected" : ""}${Number(color.stock) < 1 ? " is-sold-out" : ""}`}
                        onClick={() => chooseColor(color)}
                      >
                        {color.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className={`stock-note${available ? "" : " stock-note--out"}`}>
            <span />
            {available
              ? `${availableStock} in stock${selectedVariant ? ` · ${selectedVariant.name}` : ""}${selectedColor ? ` · ${selectedColor.name}` : ""}`
              : colors.length && !selectedColor
                ? "Choose a color"
                : "Currently out of stock"}
          </div>

          <div className="product-info__buy">
            <div className="quantity-control">
              <button
                aria-label="Decrease quantity"
                disabled={quantity <= 1}
                onClick={() =>
                  setQuantity((current) => Math.max(1, current - 1))
                }
              >
                <Minus size={15} />
              </button>
              <span>{quantity}</span>
              <button
                aria-label="Increase quantity"
                disabled={quantity >= availableStock}
                onClick={() => setQuantity((current) => current + 1)}
              >
                <Plus size={15} />
              </button>
            </div>
            <button
              className="button button--quiet product-info__cart"
              disabled={
                !available ||
                (variants.length > 0 && !selectedVariant) ||
                (colors.length > 0 && !selectedColor)
              }
              onClick={add}
            >
              Add to Cart
            </button>
            <button
              className="button button--primary product-info__buy-now"
              disabled={
                !available ||
                (variants.length > 0 && !selectedVariant) ||
                (colors.length > 0 && !selectedColor)
              }
              onClick={buyNow}
            >
              Buy it now <ArrowRight size={17} />
            </button>
            <button
              className={`icon-button product-info__save${wishlistIds.includes(String(product._id)) ? " is-active" : ""}`}
              onClick={save}
              aria-label="Save product"
            >
              <Heart
                size={19}
                fill={
                  wishlistIds.includes(String(product._id))
                    ? "currentColor"
                    : "none"
                }
              />
            </button>
          </div>

          {product.description && (
            <div className="product-info__description">
              <h5>Description</h5>

              <p>{product.description}</p>
            </div>
          )}

          <div className="product-promises">
            <div>
              <Truck size={18} />
              <span>Fast express delivery across India (24-48h dispatch)</span>
            </div>
            <div>
              <ShieldCheck size={18} />
              <span>
                100% Genuine product & secure 256-bit encrypted checkout
              </span>
            </div>
            <div>
              <RotateCcw size={18} />
              <span>Hassle-free 7-day replacement guarantee</span>
            </div>
          </div>
        </section>
      </div>
      <section className="reviews-section">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Notes from the community</p>
            <h2>Reviews</h2>
          </div>
          <span className="review-average">
            ★ {Number(product.ratings || 0).toFixed(1)} / 5
          </span>
        </div>
        {reviews.length > 0 ? (
          <div className="review-grid">
            {reviews.slice(0, 3).map((review) => (
              <article className="review-card" key={review._id}>
                <div className="review-card__stars">
                  {"★".repeat(Number(review.rating || 0))}
                </div>
                <p>“{review.comment}”</p>
                <span>
                  {review.user?.name || review.name || "Customer"}
                  {review.verifiedPurchase && (
                    <small> · Verified purchase</small>
                  )}
                </span>
              </article>
            ))}
          </div>
        ) : (
          <p className="review-empty">
            No reviews yet. Be the first to share your experience.
          </p>
        )}
        {token ? (
          <form className="review-form" onSubmit={saveReview}>
            <div className="review-form__heading">
              <div>
                <span className="eyebrow">Your experience</span>
                <h3>{myReview ? "Edit your review" : "Write a review"}</h3>
              </div>
              {myReview && (
                <span className="review-form__verified">
                  You have reviewed this product
                </span>
              )}
            </div>
            <div
              className="review-form__rating"
              role="radiogroup"
              aria-label="Your rating"
            >
              {[1, 2, 3, 4, 5].map((rating) => (
                <button
                  key={rating}
                  type="button"
                  role="radio"
                  aria-checked={reviewRating === rating}
                  aria-label={`${rating} star${rating === 1 ? "" : "s"}`}
                  disabled={reviewBusy}
                  onClick={() => setReviewRating(rating)}
                >
                  {rating <= reviewRating ? "★" : "☆"}
                </button>
              ))}
              <span>{reviewRating} / 5</span>
            </div>
            <label className="field-label" htmlFor="product-review-comment">
              Review
            </label>
            <textarea
              id="product-review-comment"
              className="text-field review-form__comment"
              value={reviewComment}
              onChange={(event) => setReviewComment(event.target.value)}
              maxLength={1000}
              rows={4}
              placeholder="What did you think of this product?"
              required
            />
            {reviewError && <p className="form-error">{reviewError}</p>}
            {reviewMessage && (
              <p className="review-form__success" role="status">
                {reviewMessage}
              </p>
            )}
            <button className="button button--primary" disabled={reviewBusy}>
              {reviewBusy
                ? "Saving…"
                : myReview
                  ? "Update review"
                  : "Submit review"}
            </button>
          </form>
        ) : (
          <p className="review-signin">
            Please{" "}
            <Link to="/login" state={{ from: location.pathname }}>
              sign in
            </Link>{" "}
            to write a review.
          </p>
        )}
      </section>
      <div className="product-back-link">
        <Link className="text-link" to="/shop">
          <ArrowLeft size={16} /> Back to all finds
        </Link>
        <span>{reviews.length} customer notes</span>
      </div>
    </main>
  );
}
